// src/components/GlobalLoader.jsx
//
// Renders a slim animated bar at the very top of the viewport — similar to
// GitHub / YouTube / Notion — ONLY during mutations (POST, PUT, DELETE).
//
// It never uses a fullscreen white overlay, so Sidebar, Header, and Footer
// are always visible. GET requests must NOT trigger this; use per-page
// skeleton loaders inside the Outlet instead.

import React, { useEffect, useRef, useState } from "react";
import { useLoading } from "../contexts/LoadingContext";

const BAR_COLOR = "linear-gradient(90deg, #7c5cf0, #a78bfa, #7c5cf0)";

const GlobalLoader = () => {
  const { loading: mutationLoading, message: mutationMessage } = useLoading();

  // Animate progress from 0 → ~85 % while loading, then sprint to 100 % on done
  const [progress, setProgress]   = useState(0);
  const [visible,  setVisible]    = useState(false);
  const [fadeOut,  setFadeOut]    = useState(false);
  const timerRef                  = useRef(null);
  const frameRef                  = useRef(null);

  useEffect(() => {
    if (mutationLoading) {
      setProgress(0);
      setFadeOut(false);
      setVisible(true);

      // Ease toward 85 % — never reaches 100 on its own
      let p = 0;
      const tick = () => {
        p += (85 - p) * 0.04;
        setProgress(p);
        frameRef.current = requestAnimationFrame(tick);
      };
      frameRef.current = requestAnimationFrame(tick);
    } else {
      // Sprint to 100 then fade out
      cancelAnimationFrame(frameRef.current);
      setProgress(100);
      timerRef.current = setTimeout(() => {
        setFadeOut(true);
        timerRef.current = setTimeout(() => setVisible(false), 300);
      }, 200);
    }

    return () => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(timerRef.current);
    };
  }, [mutationLoading]);

  if (!visible) return null;

  return (
    <>
      {/* ── Slim top bar ── */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
        aria-label={mutationMessage || "Saving…"}
        style={{
          position:   "fixed",
          top:        0,
          left:       0,
          right:      0,
          height:     3,
          zIndex:     99999,
          opacity:    fadeOut ? 0 : 1,
          transition: "opacity 0.3s ease",
          pointerEvents: "none",
        }}
      >
        <div style={{
          height:     "100%",
          width:      `${progress}%`,
          background: BAR_COLOR,
          backgroundSize: "200% 100%",
          animation:  "shimmer 1.4s linear infinite",
          transition: "width 0.1s linear",
        }} />
      </div>

      {/* ── Optional small pill message (bottom-right, non-blocking) ── */}
      {mutationMessage && (
        <div
          aria-live="polite"
          style={{
            position:     "fixed",
            bottom:       20,
            right:        20,
            zIndex:       99998,
            background:   "rgba(26,20,56,0.92)",
            color:        "#e8e3ff",
            fontSize:     12,
            fontWeight:   500,
            padding:      "7px 14px",
            borderRadius: 20,
            border:       "0.5px solid rgba(124,92,240,0.35)",
            display:      "flex",
            alignItems:   "center",
            gap:          8,
            opacity:      fadeOut ? 0 : 1,
            transition:   "opacity 0.3s ease",
            pointerEvents: "none",
          }}
        >
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: "#7c5cf0",
            animation: "pulse 0.8s infinite ease-in-out",
          }} />
          {mutationMessage}
        </div>
      )}

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1);   opacity: 0.5; }
          50%       { transform: scale(1.6); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default GlobalLoader;
